/**
 * Date and time utilities
 */

/**
 * Get current year
 */
export function getCurrentYear(): number {
  return new Date().getFullYear()
}

/**
 * Format date for display.
 * Returns an empty string for missing or unparseable dates so callers never
 * render the literal "Invalid Date" produced by toLocaleDateString.
 */
export function formatDate(date: string): string {
  if (!date) return ""

  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return ""

  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

/**
 * Format date range
 */
export function formatDateRange(startDate: string, endDate?: string): string {
  const start = new Date(startDate)
  const startFormatted = start.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  if (!endDate) return startFormatted

  const end = new Date(endDate)
  const endFormatted = end.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short", 
    day: "numeric",
  })

  return `${startFormatted} - ${endFormatted}`
}

/**
 * Check if date is in the future
 */
export function isFutureDate(date: string): boolean {
  return new Date(date) > new Date()
}

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 */
export function getRelativeTime(date: string): string {
  const now = new Date()
  const target = new Date(date)
  const diffInMs = target.getTime() - now.getTime()
  const diffInHours = diffInMs / (1000 * 60 * 60)
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

  if (Math.abs(diffInDays) >= 1) {
    const days = Math.floor(Math.abs(diffInDays))
    return diffInMs > 0 ? `in ${days} day${days > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''} ago`
  } else if (Math.abs(diffInHours) >= 1) {
    const hours = Math.floor(Math.abs(diffInHours))
    return diffInMs > 0 ? `in ${hours} hour${hours > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else {
    return diffInMs > 0 ? 'soon' : 'just now'
  }
}

